import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    CircularProgress,
    Stack,
    Paper,
    Typography,
    Chip,
    Card,
    CardContent,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { linksAPI } from '../services/api';

interface LinkDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    linkId: number | null;
    title: string;
}

const LinkDetailsDialog: React.FC<LinkDetailsDialogProps> = ({ open, onClose, linkId, title }) => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState<any>(null);

    useEffect(() => {
        if (open && linkId) {
            fetchDetails();
        } else {
            setDetails(null);
        }
    }, [open, linkId]);

    const fetchDetails = async () => {
        if (!linkId) return;
        setLoading(true);
        try {
            const response = await linksAPI.getStats(linkId);
            setDetails(response.data);
        } catch (error) {
            console.error('Link detayları alınamadı:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'normal': return 'success';
            case 'şüpheli': return 'warning';
            case 'paylaşılmış': return 'error';
            default: return 'default';
        }
    };

    const groupLogsBySession = (logs: any[]) => {
        if (!logs || logs.length === 0) return [];

        const sortedLogs = [...logs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const sessions: any[] = [];
        let currentSession: any[] = [];

        sortedLogs.forEach((log, index) => {
            currentSession.push(log);
            if (log.event_type === 'open' || index === sortedLogs.length - 1) {
                sessions.push(currentSession);
                currentSession = [];
            }
        });

        return sessions;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                {title}
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: '70vh' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                ) : details ? (
                    <Stack spacing={3}>
                        {/* Üst Bilgiler */}
                        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Durum</Typography>
                                <Chip
                                    label={details?.sharing_status?.toUpperCase()}
                                    color={getStatusColor(details?.sharing_status) as any}
                                    sx={{ mt: 1 }}
                                />
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">İzlenme</Typography>
                                <Typography variant="h6">
                                    {details.has_completed ? 'Tamamlandı' : details.has_started ? 'Başladı' : details.has_opened ? 'Açıldı' : 'İzlenmedi'}
                                </Typography>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Şehir Sayısı</Typography>
                                <Typography variant="h6">{details.cities_used}</Typography>
                            </Paper>
                        </Box>

                        {/* İstatistik Özetleri */}
                        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 100, textAlign: 'center', borderLeft: '4px solid #1976d2' }}>
                                <Typography variant="body2" color="text.secondary">Toplam Oturum</Typography>
                                <Typography variant="h6" color="primary.main">{groupLogsBySession(details.logs).length}</Typography>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 100, textAlign: 'center', borderLeft: '4px solid #ed6c02' }}>
                                <Typography variant="body2" color="text.secondary">Sayfa Açılışı</Typography>
                                <Typography variant="h6" sx={{ color: '#ed6c02' }}>{details.logs.filter((l: any) => l.event_type === 'open').length}</Typography>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 100, textAlign: 'center', borderLeft: '4px solid #2e7d32' }}>
                                <Typography variant="body2" color="text.secondary">Tam İzlenme (%100)</Typography>
                                <Typography variant="h6" color="success.main">
                                    {details.logs.filter((l: any) => (l.event_type === 'end' || (l.event_type === 'progress' && l.watch_time === 100))).length}
                                </Typography>
                            </Paper>
                        </Box>

                        {/* Log Listesi (Gruplanmış) */}
                        <Typography variant="h6">İzleme Hareketleri</Typography>
                        <Box>
                            {groupLogsBySession(details.logs).map((session, sIndex) => {
                                const startDate = new Date(session[session.length - 1].created_at);
                                return (
                                    <Card key={sIndex} variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Oturum Başlangıcı: {startDate.toLocaleString('tr-TR')}
                                            </Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Zaman</TableCell>
                                                            <TableCell>Olay</TableCell>
                                                            <TableCell>İlerleme</TableCell>
                                                            <TableCell>Cihaz</TableCell>
                                                            <TableCell>Şehir</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {session.map((log: any, lIndex: number) => (
                                                            <TableRow key={lIndex}>
                                                                <TableCell>{new Date(log.created_at).toLocaleTimeString('tr-TR')}</TableCell>
                                                                <TableCell>
                                                                    {log.event_type === 'open' && <Chip label="Açıldı" size="small" />}
                                                                    {log.event_type === 'play' && <Chip label="Başladı" color="primary" size="small" />}
                                                                    {log.event_type === 'progress' && <Chip label="İzleniyor" color="info" size="small" />}
                                                                    {log.event_type === 'end' && <Chip label="Bitti" color="success" size="small" />}
                                                                </TableCell>
                                                                <TableCell>{log.watch_time > 0 ? `%${log.watch_time}` : '-'}</TableCell>
                                                                <TableCell>{log.browser} / {log.os}</TableCell>
                                                                <TableCell>{log.city}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                            {details.logs.length === 0 && (
                                <Typography align="center">Henüz hareket yok.</Typography>
                            )}
                        </Box>
                    </Stack>
                ) : (
                    <Typography>Veri bulunamadı.</Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default LinkDetailsDialog;
